import {
  keepPreviousData,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { nodeGroupApi } from "../services/api";
import {
  NodeGroup,
  NodeGroupCreate,
  NodeGroupListResponse,
  NodeGroupUpdate,
} from "../types/node_group";

export const useNodeGroups = (
  filters: { page?: number; page_size?: number; search?: string } = {},
) => {
  return useQuery({
    queryKey: ["node_groups", filters],
    queryFn: () => nodeGroupApi.getGroups(filters),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
};

export const useNodeGroup = (id: number) => {
  return useQuery({
    queryKey: ["node_group", id],
    queryFn: () => nodeGroupApi.getGroup(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateNodeGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: NodeGroupCreate) => nodeGroupApi.createGroup(data),

    onSuccess: (row: NodeGroup) => {
      queryClient.invalidateQueries({
        queryKey: ["node_groups"],
      });
      toast.success(`Global Flow "${row.name}" created`);
    },

    onError: (error: any) => {
      const msg =
        error.response?.data?.detail || "Failed to create global flow";
      toast.error(msg);
    },
  });
};

export const useUpdateNodeGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: NodeGroupUpdate }) =>
      nodeGroupApi.updateGroup(id, data),

    onSuccess: (row: NodeGroup) => {
      queryClient.setQueryData(["node_group", row.id], row);
      queryClient.invalidateQueries({
        queryKey: ["node_groups"],
      });
      toast.success(`Global Flow "${row.name}" updated`);
    },

    onError: (error: any) => {
      const msg =
        error.response?.data?.detail || "Failed to update global flow";
      toast.error(msg);
    },
  });
};

export const useDeleteNodeGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => nodeGroupApi.deleteGroup(id),

    onSuccess: (_: void, id: number) => {
      queryClient.removeQueries({
        queryKey: ["node_group", id],
      });
      queryClient.invalidateQueries({
        queryKey: ["node_groups"],
      });
      toast.success("Global Flow deleted");
    },

    onError: (error: any) => {
      const msg =
        error.response?.data?.detail || "Failed to delete global flow";
      toast.error(msg);
    },
  });
};
